/**
 * PUBLIC ASR speech-check harness — /speech-check?tag=pilot1
 *
 * Everything the product judges sits on the transcript, and the transcript comes from the
 * browser's free Web Speech API. We have measured judge fairness on CLEAN TEXT and never
 * measured whether Indian English on real phones survives transcription. Volunteers (friends /
 * family) open a shared link on their OWN phones, read three short passages aloud, speak freely
 * for ~30s, and we get a WER number per passage BEFORE the first pilot rides on it.
 *
 * HONESTY CONTRACT — the page KEEPS these promises, the code makes them true:
 *  - TEXT ONLY. Nothing in this file (or its imports) ever records or uploads audio. The mic
 *    warm-up computes an RMS level locally and throws the samples away; SpeechRecognition is
 *    the browser's own engine and we only ever see its text output. No MediaRecorder anywhere.
 *  - No account, no name. The only free-text field is an OPTIONAL "how would you describe your
 *    accent / first language" note. No PII is collected; the server keeps UA + tag + text only.
 *  - The results measure OUR transcription quality, never the volunteer — the page says so on
 *    the intro AND on the thank-you screen, next to their own numbers.
 *
 * Deliberately self-contained: the interview's speech stack (candidate/InterviewStep.tsx) is
 * load-bearing and untouched. This is a LEAN re-implementation of the same hard-won guards:
 * monotonic generation counter (a superseded recogniser's callbacks must never touch state),
 * orphan-then-abort teardown, restart-on-end with exponential backoff + give-up, never restart
 * into a hidden tab, fatal-vs-transient error split, confidence averaged only over real values.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { wordErrorRate, SPEECH_CHECK_PASSAGES } from "@talenthop/shared";
import { Check, Info } from "../../components/icons.js";
import { CandidateTopBar } from "../candidate/CandidateChrome.js";

/* ================================ contract shapes ================================ */

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const SUBMIT_URL = `${API_BASE}/speech-check`;

/** One row of POST /api/speech-check `results` — exact cross-agent contract shape. */
interface SectionResult {
  passageId: string;
  reference: string | null;
  transcript: string;
  confidenceAvg: number | null;
  restarts: number;
  durationMs: number;
}

interface SubmitPayload {
  submissionId: string;
  tag: string;
  deviceLabel?: string;
  accentNote?: string;
  results: SectionResult[];
}

/* ================================ the passages ================================
 * PAGE CONSTANTS shared with nothing — the reference text IS the ground truth the server
 * scores against, so it must never drift from what the volunteer actually read. Natural
 * sentences a person can read aloud comfortably: we are measuring the engine, not the reader. */

// Canonical texts live in shared (SPEECH_CHECK_PASSAGES) — the server scores against THAT copy
// and ignores whatever reference this page sends, so the two can never drift and a tampered page
// cannot flatter the measurement. This constant is a view, not a source.
const PASSAGES = SPEECH_CHECK_PASSAGES;

/** One id per page session, sent with the submit AND the abandon beacon. The server upserts on
 *  (submission_id, passage_id), so a beacon racing the submit — or a retry after a success whose
 *  response was lost — can never double-count a volunteer in a ten-person pilot. */
const SUBMISSION_ID = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

const FREE_SPEECH_ID = "free-speech";
const FREE_SPEECH_PROMPT =
  "Describe your current or most recent work — what you do day to day, the tools you use, and " +
  "one thing you worked on recently that you are proud of.";

/** Free speech: aim 30–45s; auto-finish at 60s so a distracted volunteer's mic never runs on. */
const FREE_SPEECH_TARGET_S = 30;
const FREE_SPEECH_CAP_MS = 60_000;
/** Read-aloud safety cap — nobody needs 2.5 minutes for 55 words; the mic must not run forever. */
const PASSAGE_CAP_MS = 150_000;

/* ====================== display word-accuracy (shared WER, client-side) ======================
 * The thank-you screen computes "we heard N of M words" with the SAME canonical implementation
 * the server scores with (packages/shared/src/wer.ts) — display-only convenience; the server
 * still recomputes from reference+transcript and its number is the record. */

/** Words of the reference the engine got right: refTokens − substitutions − deletions. */
function correctWords(reference: string, transcript: string): { correct: number; refTokens: number } {
  const w = wordErrorRate(reference, transcript);
  return { correct: Math.max(0, w.refTokens - w.substitutions - w.deletions), refTokens: w.refTokens };
}

/* ====================== minimal Web Speech typings (not in lib.dom) ====================== */

interface RecAlternative {
  transcript: string;
  confidence?: number;
}
interface RecResult {
  0: RecAlternative;
  isFinal: boolean;
  length: number;
}
interface RecResultList {
  length: number;
  [index: number]: RecResult;
}
interface RecEvent {
  resultIndex: number;
  results: RecResultList;
}
interface RecErrorEvent {
  error?: string;
}
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: RecEvent) => void) | null;
  onerror: ((e: RecErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => RecognitionLike;
function recognitionCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/* ============================== lean check recogniser ============================== */

/** `not-allowed` / `service-not-allowed` mean the mic is not coming back; retrying re-prompts
 *  the volunteer forever. `no-speech`, `aborted`, `network` are transient — onend decides. */
function isFatalRecognitionError(code: string | undefined): boolean {
  return code === "not-allowed" || code === "service-not-allowed";
}

const RESTART_BASE_MS = 250;
const RESTART_MAX_MS = 4_000;
/** A session that dies this fast having heard nothing is a failure, not a thoughtful pause. */
const PRODUCTIVE_SESSION_MS = 1_500;
const MAX_CONSECUTIVE_FAILURES = 6;

interface SectionSnapshot {
  transcript: string;
  confidenceAvg: number | null;
  restarts: number;
}

interface CheckRecogniser {
  supported: boolean;
  /** Mic denied or the recogniser refused to stay up — the section can't record. */
  blocked: boolean;
  listening: boolean;
  finalText: string;
  interim: string;
  /** Start a fresh section: accumulators reset, intent up. */
  begin: () => void;
  /** End the section: intent down, recogniser torn down, numbers returned. */
  end: () => SectionSnapshot;
  /** Read the live numbers WITHOUT stopping — for the abandon beacon only. */
  peek: () => SectionSnapshot;
  /** Clear a blocked verdict (volunteer fixed the permission) and start again. */
  retry: () => void;
}

function useCheckRecogniser(): CheckRecogniser {
  const Ctor = useMemo(() => recognitionCtor(), []);
  const supported = Ctor !== null;

  const [listening, setListening] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [finalText, setFinalText] = useState("");
  const [interim, setInterim] = useState("");

  const recRef = useRef<RecognitionLike | null>(null);
  // Monotonic generation — the load-bearing guard. Every teardown bumps it; a callback from a
  // superseded recogniser compares and walks away. Without this, an abandoned recogniser keeps
  // the mic open and appends the same sentence twice.
  const genRef = useRef(0);
  const runningRef = useRef(false);
  const intentRef = useRef(false);
  const blockedRef = useRef(false);
  const failuresRef = useRef(0);
  const sessionStartRef = useRef(0);
  const gotResultRef = useRef(false);
  const restartTimerRef = useRef<number | null>(null);
  const restartsRef = useRef(0);
  const finalRef = useRef("");
  const interimRef = useRef("");
  const confSumRef = useRef(0);
  const confCountRef = useRef(0);
  const startRef = useRef<(isRestart: boolean) => void>(() => undefined);

  const clearRestartTimer = useCallback(() => {
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
  }, []);

  /** Orphan AND abort — both halves matter (see generation comment above). */
  const teardown = useCallback(() => {
    genRef.current += 1;
    const old = recRef.current;
    recRef.current = null;
    runningRef.current = false;
    if (old) {
      old.onresult = null;
      old.onerror = null;
      old.onend = null;
      try {
        old.abort();
      } catch {
        /* already dead */
      }
    }
  }, []);

  const block = useCallback(() => {
    blockedRef.current = true;
    intentRef.current = false;
    setBlocked(true);
    setListening(false);
  }, []);

  const scheduleRestart = useCallback(
    (delay: number) => {
      clearRestartTimer();
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        startRef.current(true);
      }, delay);
    },
    [clearRestartTimer],
  );

  const startNow = useCallback(
    (isRestart: boolean) => {
      if (!Ctor || runningRef.current || blockedRef.current || !intentRef.current) return;
      // A hidden tab starves recognition; restarting into one is a guaranteed retry storm. The
      // visibilitychange effect below puts the mic back when the volunteer returns.
      if (typeof document !== "undefined" && document.hidden) return;
      clearRestartTimer();
      teardown(); // never leave a predecessor holding the mic
      const gen = genRef.current;

      const rec = new Ctor();
      rec.lang = "en-IN";
      rec.continuous = true;
      rec.interimResults = true;

      rec.onresult = (e: RecEvent) => {
        if (gen !== genRef.current) return; // superseded — its words are not this section's
        let fin = "";
        let intr = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const r = e.results[i];
          if (!r) continue;
          if (r.isFinal) {
            fin += r[0].transcript;
            // Chrome reports a real 0–1 confidence on finals; Safari/others report 0 or
            // nothing. Average only genuine measurements — null is honest, 0 would be a lie.
            const c = r[0].confidence;
            if (typeof c === "number" && c > 0) {
              confSumRef.current += c;
              confCountRef.current += 1;
            }
          } else {
            intr += r[0].transcript;
          }
        }
        if (fin || intr) gotResultRef.current = true;
        if (fin) {
          finalRef.current = `${finalRef.current} ${fin}`.trim();
          setFinalText(finalRef.current);
        }
        interimRef.current = intr;
        setInterim(intr);
      };

      rec.onerror = (e: RecErrorEvent) => {
        if (gen !== genRef.current) return;
        if (isFatalRecognitionError(e.error)) block();
        // onend always follows — the restart decision lives there, in one place.
      };

      rec.onend = () => {
        if (gen !== genRef.current) return;
        runningRef.current = false;
        setListening(false);
        interimRef.current = "";
        setInterim("");
        if (!intentRef.current) return; // the section ended on purpose
        // Chrome ends recognition by itself after a stretch of silence — normal, and it must
        // NOT count against the retry budget or a thoughtful pause kills the mic.
        const sessionMs = Date.now() - sessionStartRef.current;
        const productive = gotResultRef.current || sessionMs >= PRODUCTIVE_SESSION_MS;
        failuresRef.current = productive ? 0 : failuresRef.current + 1;
        if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
          block();
          return;
        }
        if (typeof document !== "undefined" && document.hidden) return; // resume on return
        scheduleRestart(
          failuresRef.current === 0
            ? RESTART_BASE_MS
            : Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** failuresRef.current),
        );
      };

      recRef.current = rec;
      sessionStartRef.current = Date.now();
      gotResultRef.current = false;
      try {
        rec.start();
        runningRef.current = true;
        setListening(true);
        if (isRestart) restartsRef.current += 1; // an honest count of engine drop-outs
      } catch {
        // Chrome throws InvalidStateError if the previous recogniser hasn't released the mic.
        runningRef.current = false;
        setListening(false);
        failuresRef.current += 1;
        if (failuresRef.current >= MAX_CONSECUTIVE_FAILURES) block();
        else if (intentRef.current)
          scheduleRestart(Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** failuresRef.current));
      }
    },
    [Ctor, block, clearRestartTimer, scheduleRestart, teardown],
  );
  startRef.current = startNow;

  const begin = useCallback(() => {
    finalRef.current = "";
    interimRef.current = "";
    setFinalText("");
    setInterim("");
    confSumRef.current = 0;
    confCountRef.current = 0;
    restartsRef.current = 0;
    failuresRef.current = 0;
    intentRef.current = true;
    startNow(false);
  }, [startNow]);

  const snapshot = useCallback(
    (): SectionSnapshot => ({
      transcript: `${finalRef.current} ${interimRef.current}`.trim(),
      confidenceAvg: confCountRef.current > 0 ? confSumRef.current / confCountRef.current : null,
      restarts: restartsRef.current,
    }),
    [],
  );

  const end = useCallback((): SectionSnapshot => {
    const snap = snapshot();
    intentRef.current = false;
    clearRestartTimer();
    teardown();
    setListening(false);
    setInterim("");
    return snap;
  }, [snapshot, clearRestartTimer, teardown]);

  const retry = useCallback(() => {
    blockedRef.current = false;
    setBlocked(false);
    failuresRef.current = 0;
    intentRef.current = true;
    startNow(false);
  }, [startNow]);

  // The volunteer switching apps mid-passage must not silently kill the section: resume the
  // recogniser (counted as a restart — the gap is real and belongs in the data).
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && intentRef.current && !runningRef.current && !blockedRef.current) {
        startRef.current(true);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  useEffect(
    () => () => {
      clearRestartTimer();
      teardown();
    },
    [clearRestartTimer, teardown],
  );

  return useMemo(
    () => ({ supported, blocked, listening, finalText, interim, begin, end, peek: snapshot, retry }),
    [supported, blocked, listening, finalText, interim, begin, end, snapshot, retry],
  );
}

/* ============================== mic warm-up (intro only) ==============================
 * A live "we can hear you" level so the permission prompt happens BEFORE passage timing starts.
 * Local RMS over an AnalyserNode — the samples never leave this function, nothing is recorded.
 * Torn down before the first passage so SpeechRecognition has the mic to itself (iOS is touchy
 * about two owners). Generation counter guards the async getUserMedia against stop-mid-open —
 * without it the stream resolves into a dead hook and the OS mic light stays on forever. */

function useMicWarmup(): {
  supported: boolean;
  active: boolean;
  denied: boolean;
  level: number;
  start: () => void;
  stop: () => void;
} {
  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof AudioContext !== "undefined";
  const [level, setLevel] = useState(0);
  const [active, setActive] = useState(false);
  const [denied, setDenied] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number | null>(null);
  const genRef = useRef(0);
  const openingRef = useRef(false);
  const lastPublishRef = useRef(0);

  const stop = useCallback(() => {
    genRef.current += 1;
    openingRef.current = false;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    setActive(false);
    setLevel(0);
  }, []);

  const start = useCallback(() => {
    if (!supported || streamRef.current || openingRef.current) return;
    const gen = genRef.current;
    openingRef.current = true;
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        openingRef.current = false;
        if (gen !== genRef.current) {
          stream.getTracks().forEach((t) => t.stop()); // stopped mid-open — release immediately
          return;
        }
        streamRef.current = stream;
        const ctx = new AudioContext();
        ctxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (gen !== genRef.current) return;
          analyser.getByteTimeDomainData(data);
          let sumSq = 0;
          for (let i = 0; i < data.length; i++) {
            const v = ((data[i] ?? 128) - 128) / 128;
            sumSq += v * v;
          }
          const rms = Math.sqrt(sumSq / data.length);
          const next = Math.max(0, Math.min(1, rms * 3.2)); // speech RMS sits well under 1
          const now = performance.now();
          if (now - lastPublishRef.current >= 60) {
            lastPublishRef.current = now;
            setLevel(next);
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        setActive(true);
        tick();
      })
      .catch(() => {
        openingRef.current = false;
        if (gen === genRef.current) setDenied(true);
      });
  }, [supported]);

  useEffect(() => () => stop(), [stop]);
  return { supported, active, denied, level, start, stop };
}

/* ==================================== helpers ==================================== */

/** ?tag= from the shared link, clamped to the contract's [a-z0-9-] ≤40. Default "pilot". */
function tagFromUrl(): string {
  const raw = new URLSearchParams(window.location.search).get("tag") ?? "pilot";
  const clean = raw.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 40);
  return clean || "pilot";
}

/** Coarse "Chrome · Android" label for the row list — the server keeps the full UA itself. */
function deviceLabel(): string {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const os = /iphone|ipad|ipod/i.test(ua)
    ? "iOS"
    : /android/i.test(ua)
      ? "Android"
      : /mac os/i.test(ua)
        ? "macOS"
        : /windows/i.test(ua)
          ? "Windows"
          : "other";
  const browser = /edg\//i.test(ua)
    ? "Edge"
    : /samsungbrowser/i.test(ua)
      ? "Samsung Internet"
      : /crios/i.test(ua)
        ? "Chrome"
        : /fxios|firefox/i.test(ua)
          ? "Firefox"
          : /chrome/i.test(ua)
            ? "Chrome"
            : /safari/i.test(ua)
              ? "Safari"
              : "other";
  return `${browser} · ${os}`.slice(0, 80);
}

function buildPayload(tag: string, accentNote: string, results: SectionResult[]): SubmitPayload {
  const payload: SubmitPayload = {
    // One id per page session, on the submit AND the abandon beacon: the server upserts on
    // (submission_id, passage_id), so a beacon racing the submit — or a retry after a success
    // whose response was lost — can never double-count a volunteer in a ten-person pilot.
    submissionId: SUBMISSION_ID,
    tag,
    deviceLabel: deviceLabel(),
    results: results.slice(0, 6).map((r) => ({ ...r, transcript: r.transcript.slice(0, 4000) })),
  };
  const note = accentNote.trim().slice(0, 120);
  if (note) payload.accentNote = note;
  return payload;
}

/* ==================================== the page ==================================== */

type Step =
  | { kind: "intro" }
  | { kind: "passage"; idx: number }
  | { kind: "free" }
  | { kind: "outro" };

type SubmitState = "sending" | "ok" | "error";

const PULSE_CSS = `
@keyframes sc-pulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .45; transform: scale(.8); } }
.sc-dot { width: 10px; height: 10px; border-radius: 50%; background: var(--crit); animation: sc-pulse 1.3s ease-in-out infinite; flex: none; }
`;

export function SpeechCheck(): ReactNode {
  const rec = useCheckRecogniser();
  const { peek } = rec; // stable — the beacon listener must not re-register per transcript update
  const warmup = useMicWarmup();
  const tag = useMemo(() => tagFromUrl(), []);

  const [step, setStep] = useState<Step>({ kind: "intro" });
  const [recording, setRecording] = useState(false);
  const [elapsedS, setElapsedS] = useState(0);
  const [accentNote, setAccentNote] = useState("");
  const [micHeard, setMicHeard] = useState(false);
  const [results, setResults] = useState<SectionResult[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>("sending");
  const [copied, setCopied] = useState(false);

  // Refs mirrored for the abandon beacon (pagehide fires outside React's render cycle).
  const resultsRef = useRef<SectionResult[]>([]);
  const accentRef = useRef("");
  const stepRef = useRef<Step>({ kind: "intro" });
  const recordingRef = useRef(false);
  const startedAtRef = useRef(0);
  const submittedRef = useRef(false);
  const beaconSentRef = useRef(false);
  accentRef.current = accentNote;
  stepRef.current = step;
  // recordingRef is NOT synced from state here: start/finishSection own it synchronously so the
  // double-finish guard can't be undone by a render carrying a stale `recording` value.

  // "We can hear you" — latches on the first clearly-audible frame of the warm-up.
  useEffect(() => {
    if (warmup.level > 0.16) setMicHeard(true);
  }, [warmup.level]);

  /* -------- section start / finish -------- */

  const sectionMeta = useCallback((s: Step): { passageId: string; reference: string | null } | null => {
    if (s.kind === "passage") {
      const p = PASSAGES[s.idx];
      return p ? { passageId: p.id, reference: p.text } : null;
    }
    if (s.kind === "free") return { passageId: FREE_SPEECH_ID, reference: null };
    return null;
  }, []);

  const startSection = useCallback(() => {
    if (recordingRef.current) return; // double-tap guard
    recordingRef.current = true;
    startedAtRef.current = Date.now();
    setElapsedS(0);
    setRecording(true);
    rec.begin();
  }, [rec]);

  const finishSection = useCallback(() => {
    // Re-entry guard: the auto-cap interval and a "Done" tap can race in the same beat, and a
    // double finish would commit a phantom row AND skip a passage. recordingRef flips
    // synchronously (state lags a render behind).
    if (!recordingRef.current) return;
    recordingRef.current = false;
    const meta = sectionMeta(stepRef.current);
    const snap = rec.end();
    setRecording(false);
    if (meta) {
      const row: SectionResult = {
        passageId: meta.passageId,
        reference: meta.reference,
        transcript: snap.transcript.slice(0, 4000),
        confidenceAvg: snap.confidenceAvg,
        restarts: snap.restarts,
        durationMs: Math.max(0, Date.now() - startedAtRef.current),
      };
      setResults((prev) => {
        const next = [...prev, row];
        resultsRef.current = next;
        return next;
      });
    }
    setStep((s) => {
      if (s.kind === "passage") {
        return s.idx + 1 < PASSAGES.length ? { kind: "passage", idx: s.idx + 1 } : { kind: "free" };
      }
      return { kind: "outro" };
    });
  }, [rec, sectionMeta]);

  // Elapsed clock + the two safety caps (free speech 60s, read-aloud 150s). The interval
  // depends ONLY on `recording` and calls finishSection through a ref — depending on the
  // callback itself would recreate the timer on every interim-transcript render, and a timer
  // that is reborn more often than it fires is a clock that never ticks.
  const finishRef = useRef<() => void>(() => undefined);
  finishRef.current = finishSection;
  useEffect(() => {
    if (!recording) return;
    const iv = window.setInterval(() => {
      const ms = Date.now() - startedAtRef.current;
      setElapsedS(Math.floor(ms / 1000));
      const cap = stepRef.current.kind === "free" ? FREE_SPEECH_CAP_MS : PASSAGE_CAP_MS;
      if (ms >= cap) finishRef.current();
    }, 500);
    return () => window.clearInterval(iv);
  }, [recording]);

  /* -------- submit -------- */

  const submit = useCallback(async () => {
    setSubmitState("sending");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload(tag, accentRef.current, resultsRef.current)),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      submittedRef.current = true;
      setSubmitState("ok");
    } catch {
      setSubmitState("error");
    }
  }, [tag]);

  useEffect(() => {
    if (step.kind === "outro") void submit();
  }, [step.kind, submit]);

  // Abandon beacon: leaving mid-test is itself a datum. Same endpoint, fewer rows — completed
  // sections plus whatever the in-flight one heard so far. Never after a successful submit.
  useEffect(() => {
    const onPageHide = () => {
      if (submittedRef.current || beaconSentRef.current) return;
      const rows = [...resultsRef.current];
      if (recordingRef.current) {
        const meta = sectionMeta(stepRef.current);
        if (meta) {
          const snap = peek();
          rows.push({
            passageId: meta.passageId,
            reference: meta.reference,
            transcript: snap.transcript.slice(0, 4000),
            confidenceAvg: snap.confidenceAvg,
            restarts: snap.restarts,
            durationMs: Math.max(0, Date.now() - startedAtRef.current),
          });
        }
      }
      if (rows.length === 0) return; // bounced off the intro — nothing to say
      beaconSentRef.current = true;
      // text/plain dodges the CORS preflight a fire-and-forget beacon rarely survives during
      // pagehide on the exact phones being measured; the API parses the body regardless of type.
      const body = new Blob([JSON.stringify(buildPayload(tag, accentRef.current, rows))], {
        type: "text/plain",
      });
      navigator.sendBeacon?.(SUBMIT_URL, body);
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [peek, sectionMeta, tag]);

  /* -------- fallback copy (a volunteer's effort is never lost) -------- */

  const copyResults = useCallback(async () => {
    const json = JSON.stringify(buildPayload(tag, accentRef.current, resultsRef.current), null, 2);
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      // Clipboard API refused (older webviews) — the transcripts are on screen to long-press.
      setCopied(false);
      window.prompt("Copy your results:", json);
    }
  }, [tag]);

  /* ==================================== render ==================================== */

  const shellBar = <CandidateTopBar label="Speech check" />;

  const honestyFoot = (
    <p className="t1c-foot">
      Text only — no audio is recorded or leaves your device. This measures our transcription, not you.
    </p>
  );

  // Unsupported browser (Firefox, many in-app webviews): an honest dead-end, not a broken page.
  if (!rec.supported) {
    return (
      <div className="cx t1c" style={{ maxWidth: 560 }}>
        <style>{PULSE_CSS}</style>
        <div className="cx-shell">
          {shellBar}
          <div className="cx-step">
            <h1 className="t1c-h1">This browser can’t run the test</h1>
            <p style={{ color: "var(--ink-soft)" }}>
              The speech engine we need isn’t available here (this happens in Firefox and inside
              some apps’ built-in browsers). Nothing is wrong with your phone.
            </p>
            <p style={{ color: "var(--ink-soft)" }}>
              Please open this link in <b>Chrome</b> (Android) or <b>Safari</b> (iPhone) — copy it
              from the address bar or the WhatsApp message and paste it there.
            </p>
          </div>
        </div>
        {honestyFoot}
      </div>
    );
  }

  /* -------- intro -------- */
  if (step.kind === "intro") {
    return (
      <div className="cx t1c" style={{ maxWidth: 560 }}>
        <style>{PULSE_CSS}</style>
        <div className="cx-shell">
          {shellBar}
          <div className="cx-step">
            <h1 className="t1c-h1">A 3-minute speech check</h1>
            <p className="t1c-lede">
              You’ll read three short passages aloud and then speak freely for about 30 seconds.
              We measure how well our transcription hears you — that’s all.
            </p>

            <div style={{ margin: "0 0 16px" }}>
              {[
                "Text only. Your voice is turned into text by your own browser — no audio is recorded, stored, or uploaded, ever.",
                "No account, no name. Nothing on this page identifies you.",
                "The results measure OUR transcription quality — never you. There are no scores and no wrong way to speak.",
              ].map((t) => (
                <div className="consent-item" key={t.slice(0, 20)}>
                  <span style={{ color: "var(--good)", flex: "none", marginTop: 2 }}>
                    <Check />
                  </span>
                  <div className="d" style={{ fontSize: 14, color: "var(--ink-soft)" }}>{t}</div>
                </div>
              ))}
            </div>

            <label htmlFor="sc-accent" style={{ display: "block", marginBottom: 6, fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>
              Your accent or first language <span className="t1c-opt">(optional)</span>
            </label>
            <input
              id="sc-accent"
              type="text"
              maxLength={120}
              value={accentNote}
              onChange={(e) => setAccentNote(e.target.value)}
              className="input"
              placeholder="e.g. Tamil, Chennai"
              style={{ width: "100%", marginBottom: 18 }}
            />

            {/* Mic warm-up — gets the permission prompt out of the way BEFORE timing starts. */}
            <div className="t1c-sc-mic">
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <b style={{ fontSize: 14 }}>Microphone check</b>
                {micHeard && (
                  <span className="pill v">
                    We can hear you
                  </span>
                )}
              </div>
              {!warmup.active && !warmup.denied && (
                <button className="btn ghost" onClick={warmup.start} style={{ width: "100%", justifyContent: "center" }}>
                  Test my mic
                </button>
              )}
              {warmup.active && (
                <>
                  <div className="meter" style={{ margin: 0 }}>
                    <div className="bar" style={{ flex: 1 }}>
                      <i style={{ width: `${Math.round(warmup.level * 100)}%`, transition: "width 80ms linear" }} />
                    </div>
                  </div>
                  <p className="hint" style={{ margin: "8px 0 0" }}>
                    {micHeard ? "Great — your mic works." : "Say something — “hello hello” is fine."}
                  </p>
                </>
              )}
              {warmup.denied && (
                <div className="banner warn" style={{ margin: 0 }}>
                  <Info />
                  <div>
                    Microphone permission was refused. The test can’t hear you without it — allow
                    the mic for this site in your browser settings, then reload this page.
                  </div>
                </div>
              )}
            </div>

            <button
              className="btn primary t1c-cta"
              onClick={() => {
                warmup.stop(); // release the mic so the speech engine owns it alone (iOS)
                setStep({ kind: "passage", idx: 0 });
              }}
            >
              Start the check
            </button>
          </div>
        </div>
        {honestyFoot}
      </div>
    );
  }

  /* -------- read-aloud passages + free speech -------- */
  if (step.kind === "passage" || step.kind === "free") {
    const isFree = step.kind === "free";
    const passage = isFree ? null : PASSAGES[step.idx];
    const heading = isFree
      ? "Now speak freely"
      : `Passage ${step.kind === "passage" ? step.idx + 1 : 0} of ${PASSAGES.length}`;

    return (
      <div className="cx t1c" style={{ maxWidth: 560 }}>
        <style>{PULSE_CSS}</style>
        <div className="cx-shell">
          {shellBar}
          <div className="cx-step">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span className="cx-stepchip">{heading}</span>
              {passage && <span className="hint">{passage.label}</span>}
              {recording && (
                <span style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                  <span className="sc-dot" />
                  <span className="hint tnum">{elapsedS}s</span>
                </span>
              )}
            </div>

            {passage ? (
              <p
                style={{
                  fontSize: 18.5,
                  lineHeight: 1.7,
                  fontWeight: 600,
                  letterSpacing: "-.005em",
                  margin: "0 0 16px",
                }}
              >
                {passage.text}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 17, lineHeight: 1.65, fontWeight: 600, margin: "0 0 6px" }}>
                  {FREE_SPEECH_PROMPT}
                </p>
                <p className="hint" style={{ margin: "0 0 16px" }}>
                  Speak naturally for {FREE_SPEECH_TARGET_S}–45 seconds. It stops by itself at 60.
                </p>
              </>
            )}

            {rec.blocked ? (
              <div className="banner crit" style={{ margin: "0 0 12px" }}>
                <Info />
                <div>
                  We can’t reach your microphone — it may be blocked for this site, or in use by
                  another app. Allow the mic and tap retry.
                  <button className="btn sm ghost" style={{ marginLeft: 10 }} onClick={rec.retry}>
                    Retry
                  </button>
                </div>
              </div>
            ) : !recording ? (
              <button
                className="btn primary"
                style={{ width: "100%", padding: "13px 16px", fontSize: 15 }}
                onClick={startSection}
              >
                {isFree ? "Start speaking" : "Tap, then read it aloud"}
              </button>
            ) : (
              <>
                {/* The live transcript is reassurance AND honesty: the volunteer sees exactly
                    what we heard — which is exactly what will be sent, and nothing else. */}
                <div
                  style={{
                    minHeight: 92,
                    maxHeight: 180,
                    overflowY: "auto",
                    background: "var(--surface-2)",
                    border: "1px solid var(--line-soft)",
                    borderRadius: 12,
                    padding: "12px 14px",
                    fontSize: 15,
                    lineHeight: 1.6,
                    marginBottom: 12,
                  }}
                >
                  {rec.finalText || rec.interim ? (
                    <>
                      {rec.finalText}{" "}
                      <span style={{ color: "var(--ink-mute)", fontStyle: "italic" }}>{rec.interim}</span>
                    </>
                  ) : (
                    <span className="hint">Listening… what we hear will appear here.</span>
                  )}
                </div>
                <button
                  className="btn primary"
                  style={{ width: "100%", padding: "13px 16px", fontSize: 15 }}
                  onClick={finishSection}
                >
                  {isFree ? "I’m done" : "Done — next"}
                </button>
              </>
            )}
          </div>
        </div>
        {honestyFoot}
      </div>
    );
  }

  /* -------- outro: submit + their own result, honestly -------- */
  const readRows = results.filter((r) => r.reference !== null);
  const freeRow = results.find((r) => r.passageId === FREE_SPEECH_ID);

  return (
    <div className="cx t1c" style={{ maxWidth: 560 }}>
      <style>{PULSE_CSS}</style>
      <div className="cx-shell">
        {shellBar}
        <div className="cx-step" style={{ textAlign: "center" }}>
          <div className="done-check">
            <Check />
          </div>
          <h2 style={{ margin: "0 0 6px" }}>Thank you — that’s the whole test</h2>
          <p style={{ color: "var(--ink-soft)", margin: "0 0 18px" }}>
            Here is how well <b>our transcription</b> heard you. Low numbers are our problem to
            fix, never yours — finding them is exactly why this test exists.
          </p>

          <div style={{ textAlign: "left", margin: "0 0 16px" }}>
            {readRows.map((r, i) => {
              const w = r.reference ? correctWords(r.reference, r.transcript) : null;
              const label = PASSAGES.find((p) => p.id === r.passageId)?.label ?? r.passageId;
              return (
                <div
                  key={r.passageId}
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: 10,
                    padding: "10px 0",
                    borderBottom: "1px solid var(--line-soft)",
                  }}
                >
                  <b style={{ fontSize: 13.5, flex: "none" }}>
                    {i + 1}. {label}
                  </b>
                  <span className="hint" style={{ marginLeft: "auto", textAlign: "right" }}>
                    {w && w.refTokens > 0 ? (
                      <>
                        we heard <b className="tnum">{w.correct}</b> of{" "}
                        <b className="tnum">{w.refTokens}</b> words correctly (
                        {Math.round((w.correct / w.refTokens) * 100)}%)
                      </>
                    ) : (
                      "no transcript captured"
                    )}
                  </span>
                </div>
              );
            })}
            {freeRow && (
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  padding: "10px 0",
                }}
              >
                <b style={{ fontSize: 13.5, flex: "none" }}>Free speech</b>
                <span className="hint" style={{ marginLeft: "auto" }}>
                  we transcribed{" "}
                  <b className="tnum">
                    {freeRow.transcript ? freeRow.transcript.split(/\s+/).filter(Boolean).length : 0}
                  </b>{" "}
                  words in {Math.round(freeRow.durationMs / 1000)}s
                </span>
              </div>
            )}
          </div>

          {submitState === "sending" && <p className="hint">Sending your results…</p>}
          {submitState === "ok" && (
            <p className="hint">
              Results sent. You can close this page — and thank you for lending us your voice
              (well, the text of it).
            </p>
          )}
          {submitState === "error" && (
            <div style={{ textAlign: "left" }}>
              <div className="banner warn" style={{ margin: "0 0 12px" }}>
                <Info />
                <div>
                  <b>We couldn’t reach our server</b> — but your effort is not lost. Tap “Copy
                  results” and paste it back to whoever sent you this link, or try again.
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                <button className="btn primary" style={{ flex: 1 }} onClick={() => void submit()}>
                  Try again
                </button>
                <button className="btn" style={{ flex: 1 }} onClick={() => void copyResults()}>
                  {copied ? "Copied ✓" : "Copy results"}
                </button>
              </div>
              {results.map((r) => (
                <div key={r.passageId} style={{ marginBottom: 10 }}>
                  <div className="hint" style={{ marginBottom: 3 }}>
                    {PASSAGES.find((p) => p.id === r.passageId)?.label ?? "Free speech"}
                  </div>
                  <div
                    style={{
                      background: "var(--surface-2)",
                      border: "1px solid var(--line-soft)",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      lineHeight: 1.55,
                      wordBreak: "break-word",
                    }}
                  >
                    {r.transcript || <span className="hint">(nothing captured)</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {honestyFoot}
    </div>
  );
}
